import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { fetchPart, fetchRelatedParts } from "../lib/parts";
import { useCart } from "../context/CartContext";
import Seo from "../components/Seo";
import PartCard from "../components/PartCard";
import { ShoppingCart, ShieldCheck, Truck, Package, ArrowLeft } from "lucide-react";

const BASE = process.env.REACT_APP_BACKEND_URL;

const PartDetail = () => {
  const { id } = useParams();
  const { addItem } = useCart();
  const [part, setPart] = useState(null);
  const [related, setRelated] = useState([]);
  const [error, setError] = useState("");
  const [qty, setQty] = useState(1);
  const [imageIdx, setImageIdx] = useState(0);

  useEffect(() => {
    setError("");
    setPart(null);
    fetchPart(id).then(setPart).catch(() => setError("Part not found."));
    fetchRelatedParts(id, 4).then(setRelated).catch(() => {});
    window.scrollTo({ top: 0 });
  }, [id]);

  if (error) {
    return (
      <div className="vd-page" data-testid="part-not-found">
        <section className="vd-container py-20 text-center">
          <h1 className="mb-4">Part not found</h1>
          <p className="vd-muted mb-8">This part may have been removed or is no longer available.</p>
          <Link to="/parts" className="vd-btn-primary">Back to Auto Parts</Link>
        </section>
      </div>
    );
  }

  if (!part) {
    return <div className="vd-container py-16" data-testid="part-loading"><p className="vd-muted">Loading…</p></div>;
  }

  const images = (part.gallery && part.gallery.length ? part.gallery : [part.image]).filter(Boolean);
  const currentImg = images[imageIdx] || part.image;
  const imgSrc = currentImg
    ? (currentImg.startsWith("http") ? currentImg : `${BASE}${currentImg}`)
    : "";
  const inStock = part.stock > 0;

  const add = () => {
    if (!inStock) return;
    addItem({
      tyre_id: part.id,
      name: part.name,
      price: part.price,
      image: imgSrc,
      quantity: qty,
    });
  };

  return (
    <div className="vd-page" data-testid="page-part-detail">
      <Seo title={part.name} description={part.description?.slice(0, 155) || part.name} path={`/parts/${part.id}`} />

      <div className="vd-container pt-6">
        <Link to="/parts" className="inline-flex items-center gap-1.5 text-sm vd-muted hover:text-[var(--primary-red)] transition-colors" data-testid="part-back">
          <ArrowLeft size={14} /> All parts
        </Link>
      </div>

      <section className="vd-container py-8 grid grid-cols-1 lg:grid-cols-[1.1fr_1fr] gap-10">
        <div>
          <div className="vd-card overflow-hidden" style={{ background: "var(--bg-secondary)" }}>
            {imgSrc && <img src={imgSrc} alt={part.name} className="w-full aspect-[4/3] object-cover" data-testid="part-main-image" />}
          </div>
          {images.length > 1 && (
            <div className="grid grid-cols-4 gap-2 mt-3" data-testid="part-thumbs">
              {images.map((src, i) => {
                const s = src.startsWith("http") ? src : `${BASE}${src}`;
                return (
                  <button
                    key={i}
                    onClick={() => setImageIdx(i)}
                    className="aspect-[4/3] rounded-md overflow-hidden border transition-colors"
                    style={{ borderColor: i === imageIdx ? "var(--primary-red)" : "var(--border-color)" }}
                    data-testid={`part-thumb-${i}`}
                  >
                    <img src={s} alt="" className="w-full h-full object-cover" />
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <div>
          <p className="vd-muted text-xs uppercase tracking-wider mb-2">
            {part.brand || "Aftermarket"} · {part.condition}
            {part.part_number ? ` · P/N ${part.part_number}` : ""}
          </p>
          <h1 className="mb-3" data-testid="part-name">{part.name}</h1>

          <div className="flex items-baseline gap-3 mb-6">
            <span className="font-extrabold" style={{ color: "var(--primary-red)", fontSize: 36 }} data-testid="part-price">£{part.price.toFixed(2)}</span>
            <span className="text-sm font-semibold" style={{ color: inStock ? "#00A35C" : "var(--primary-red)" }} data-testid="part-stock">
              {inStock ? `In stock · ${part.stock}` : "Out of stock"}
            </span>
          </div>

          {part.fitment?.length > 0 && (
            <div className="vd-card p-4 mb-5" data-testid="part-fitment">
              <p className="vd-heading font-semibold text-sm mb-2">Fitment</p>
              <ul className="text-sm vd-body space-y-1">
                {part.fitment.map((f, i) => (
                  <li key={i}>
                    {f.make} {f.model}
                    {(f.year_from || f.year_to) ? ` (${f.year_from || "…"} – ${f.year_to || "…"})` : ""}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {part.description && <p className="vd-body mb-6" data-testid="part-description">{part.description}</p>}

          <div className="flex items-center gap-4 mb-4">
            <div className="inline-flex items-center border rounded-md" style={{ borderColor: "var(--border-color)" }}>
              <button onClick={() => setQty((q) => Math.max(1, q - 1))} className="px-3 py-2 vd-heading" data-testid="part-qty-dec">−</button>
              <span className="px-3 vd-heading font-semibold" data-testid="part-qty">{qty}</span>
              <button onClick={() => setQty((q) => Math.min(part.stock || 99, q + 1))} className="px-3 py-2 vd-heading" data-testid="part-qty-inc">+</button>
            </div>
            <button onClick={add} disabled={!inStock} className="vd-btn-primary flex-1" data-testid="part-add-to-basket">
              <ShoppingCart size={16} /> Add to Basket
            </button>
          </div>

          <ul className="mt-6 space-y-2 text-sm">
            <li className="flex items-start gap-2"><ShieldCheck size={16} className="mt-0.5" color="var(--primary-red)" /> {part.warranty || "Backed by ValueDrive warranty"}</li>
            <li className="flex items-start gap-2"><Truck size={16} className="mt-0.5" color="var(--primary-red)" /> Nationwide UK delivery available</li>
            <li className="flex items-start gap-2"><Package size={16} className="mt-0.5" color="var(--primary-red)" /> Same-day dispatch for in-stock items ordered before 3pm</li>
          </ul>
        </div>
      </section>

      {related.length > 0 && (
        <section className="vd-container pb-16" data-testid="part-related">
          <h2 className="mb-6">You might also need</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {related.map((p) => <PartCard key={p.id} part={p} />)}
          </div>
        </section>
      )}
    </div>
  );
};

export default PartDetail;
